import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Editor } from "primereact/editor";
import { FileUpload } from "primereact/fileupload";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import React, { useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import DetailPopup from "../../../components/DetailPopup/DetailPopup";
import PhotoZoom from "../../../components/PhotoZoom/PhotoZoom";
import { HakkimizdaService } from "../../../services/hakkimizda.service";
import {
  HakkimizdaBaslik,
  HakkimizdaBaslikCreateDTO,
  HakkimizdaBaslikUpdateDTO,
  HakkimizdaGorsel,
} from "../../../services/types";
import { editorHeader } from "../../../utils/commonUtil";

const validationSchema = Yup.object().shape({
  baslik: Yup.string()
    .required("Başlık alanı zorunludur")
    .max(20, "Başlık en fazla 20 karakter olmalıdır"),
  altBaslik: Yup.string()
    .optional()
    .max(120, "Alt başlık en fazla 120 karakter olmalıdır"),
  aciklama: Yup.string().required("Açıklama alanı zorunludur"),
  sira: Yup.number()
    .required("Sıra alanı zorunludur")
    .min(1, "Sıra 1'den küçük olamaz"),
});

const HakkimizdaYonetimi: React.FC = () => {
  const [items, setItems] = useState<HakkimizdaBaslik[]>([]);
  const [gorsel, setGorsel] = useState<HakkimizdaGorsel | null>(null);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [gorselDialogVisible, setGorselDialogVisible] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedItem, setSelectedItem] = useState<HakkimizdaBaslik | null>(
    null
  );
  const [photoZoomVisible, setPhotoZoomVisible] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const toast = useRef<Toast>(null);
  const [detailPopupVisible, setDetailPopupVisible] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState<Record<string, string>>(
    {}
  );

  const [formData, setFormData] = useState({
    baslik: "",
    altBaslik: "",
    aciklama: "",
    sira: 0,
  });

  const [gorselData, setGorselData] = useState({
    id: "",
    kapakGorsel: "",
    form: null as File | null,
  });

  useEffect(() => {
    loadData();
    loadGorsel();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await HakkimizdaService.baslikListele();
      if (response.data) {
        setItems(response.data);
      }
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Hata",
        detail: "Veriler yüklenirken bir hata oluştu",
      });
    }
    setLoading(false);
  };

  const loadGorsel = async () => {
    try {
      const response = await HakkimizdaService.gorselGetir();
      if (response.data && response.data.length > 0) {
        setGorsel(response.data[0]);
        setGorselData({ id: response.data[0].id, kapakGorsel: "", form: null });
      }
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Hata",
        detail: "Görsel yüklenirken bir hata oluştu",
      });
    }
  };

  const handleSubmit = async () => {
    try {
      await validationSchema.validate(formData, { abortEarly: false });

      if (editMode && selectedItem) {
        const updateData: HakkimizdaBaslikUpdateDTO = {
          id: selectedItem.id,
          baslik: formData.baslik,
          altBaslik: formData.altBaslik ? formData.altBaslik : "",
          aciklama: formData.aciklama,
          sira: formData.sira,
        };

        await HakkimizdaService.baslikGuncelle(updateData);
        toast.current?.show({
          severity: "success",
          summary: "Başarılı",
          detail: "İçerik başarıyla güncellendi",
        });
      } else {
        const createData: HakkimizdaBaslikCreateDTO = {
          baslik: formData.baslik,
          altBaslik: formData.altBaslik ? formData.altBaslik : "",
          aciklama: formData.aciklama,
          sira: formData.sira,
        };

        await HakkimizdaService.baslikEkle(createData);
        toast.current?.show({
          severity: "success",
          summary: "Başarılı",
          detail: "İçerik başarıyla eklendi",
        });
      }
      hideDialog();
      loadData();
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        error.inner.forEach((err) => {
          toast.current?.show({
            severity: "error",
            summary: "Doğrulama Hatası",
            detail: err.message,
          });
        });
        return;
      }
      toast.current?.show({
        severity: "error",
        summary: "Hata",
        detail: "İşlem sırasında bir hata oluştu",
      });
    }
  };

  const handleGorselSubmit = async () => {
    try {
      if (!gorselData.kapakGorsel) {
        toast.current?.show({
          severity: "error",
          summary: "Hata",
          detail: "Lütfen bir görsel seçiniz",
        });
        return;
      }

      await HakkimizdaService.gorselGuncelle({
        id: gorselData.id,
        kapakGorsel: gorselData.kapakGorsel,
        form: gorselData.form,
      });

      toast.current?.show({
        severity: "success",
        summary: "Başarılı",
        detail: "Görsel başarıyla güncellendi",
      });
      hideGorselDialog();
      loadGorsel();
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Hata",
        detail: "Görsel güncellenirken bir hata oluştu",
      });
    }
  };

  const confirmDelete = (item: HakkimizdaBaslik) => {
    confirmDialog({
      message: "Bu içeriği silmek istediğinizden emin misiniz?",
      header: "Silme Onayı",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Evet",
      rejectLabel: "Hayır",
      accept: () => handleDelete(item.id),
    });
  };

  const handleDelete = async (id: string) => {
    try {
      await HakkimizdaService.baslikSil(id);
      toast.current?.show({
        severity: "success",
        summary: "Başarılı",
        detail: "İçerik başarıyla silindi",
      });
      loadData();
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Hata",
        detail: "Silme işlemi sırasında bir hata oluştu",
      });
    }
  };

  const showDialog = (item?: HakkimizdaBaslik) => {
    if (item) {
      setEditMode(true);
      setSelectedItem(item);
      setFormData({
        baslik: item.baslik,
        altBaslik: item.altBaslik ? item.altBaslik : "",
        aciklama: item.aciklama,
        sira: item.sira,
      });
    } else {
      setEditMode(false);
      setSelectedItem(null);
      setFormData({
        baslik: "",
        altBaslik: "",
        aciklama: "",
        sira: 0,
      });
    }
    setDialogVisible(true);
  };

  const hideDialog = () => {
    setDialogVisible(false);
    setEditMode(false);
    setSelectedItem(null);
    setFormData({
      baslik: "",
      altBaslik: "",
      aciklama: "",
      sira: 0,
    });
  };

  const showGorselDialog = () => {
    setGorselDialogVisible(true);
  };

  const hideGorselDialog = () => {
    setGorselDialogVisible(false);
    setGorselData({
      id: gorsel?.id || "",
      kapakGorsel: "",
      form: null,
    });
  };

  const actionBodyTemplate = (rowData: HakkimizdaBaslik) => {
    return (
      <div className="flex gap-2">
        <Button
          icon="pi pi-pencil"
          rounded
          outlined
          className="mr-2"
          onClick={() => showDialog(rowData)}
        />
        <Button
          icon="pi pi-trash"
          rounded
          outlined
          severity="danger"
          onClick={() => confirmDelete(rowData)}
        />
      </div>
    );
  };

  const handleImageClick = (imageUrl: string) => {
    setSelectedImage(imageUrl);
    setPhotoZoomVisible(true);
  };

  const showDetailPopup = (rowData: HakkimizdaBaslik) => {
    setSelectedDetail({
      Başlık: rowData.baslik,
      "Alt Başlık": rowData.altBaslik || "-",
      Açıklama: rowData.aciklama,
    });
    setDetailPopupVisible(true);
  };

  const editorSettings = editorHeader();

  return (
    <div className="card p-4">
      <Toast ref={toast} />
      <ConfirmDialog />

      {/* Üst Görsel Yönetimi */}
      <div className="mb-6">
        <div className="flex justify-content-between align-items-center mb-4">
          <h2 className="text-xl">Üst Görsel</h2>
          <Button
            label="Görseli Değiştir"
            icon="pi pi-image"
            onClick={showGorselDialog}
          />
        </div>
        {gorsel && (
          <img
            src={gorsel.kapakGorsel}
            alt="Kapak Görseli"
            className="w-full max-w-30rem shadow-2 border-round cursor-pointer "
            onClick={() => handleImageClick(gorsel.kapakGorsel)}
          />
        )}
      </div>

      {/* İçerik Yönetimi */}
      <div className="flex justify-content-between align-items-center mb-4">
        <h2 className="text-xl">İçerik Yönetimi</h2>
        <Button
          label="Yeni İçerik Ekle"
          icon="pi pi-plus"
          onClick={() => showDialog()}
        />
      </div>

      <DataTable
        value={items}
        paginator
        rows={10}
        dataKey="id"
        loading={loading}
        emptyMessage="İçerik bulunamadı"
        sortField="sira"
        sortOrder={1}
      >
        <Column field="sira" header="Sıra" sortable style={{ width: "10%" }} />
        <Column
          field="baslik"
          header="Başlık"
          sortable
          style={{ width: "20%" }}
        />
        <Column
          field="altBaslik"
          header="Alt Başlık"
          sortable
          style={{ width: "30%" }}
        />
        <Column
          field="aciklama"
          header="Açıklama"
          sortable
          style={{ width: "30%" }}
          body={(rowData) => (
            <div
              className="cursor-pointer"
              style={{ whiteSpace: "pre-line" }}
              onClick={() => {
                showDetailPopup(rowData);
              }}
            >
              {rowData.aciklama.length > 100 ? (
                <div
                  dangerouslySetInnerHTML={{
                    __html: rowData.aciklama.substring(0, 100),
                  }}
                />
              ) : (
                <div dangerouslySetInnerHTML={{ __html: rowData.aciklama }} />
              )}
              <div className="cursor-pointer" style={{ color: "#ed145b" }}>
                Detaya Git &gt;
              </div>
            </div>
          )}
        />
        <Column body={actionBodyTemplate} style={{ width: "10%" }} />
      </DataTable>

      {/* İçerik Ekleme/Düzenleme Dialog */}
      <Dialog
        baseZIndex={100}
        visible={dialogVisible}
        header={editMode ? "İçerik Düzenle" : "Yeni İçerik Ekle"}
        modal
        className="p-fluid w-full md:w-6"
        onHide={hideDialog}
        contentStyle={{ overflow: "visible" }}
        style={{ overflow: "visible" }}
      >
        <div className="field mt-4">
          <label htmlFor="baslik">Başlık</label>
          <InputText
            id="baslik"
            value={formData.baslik}
            onChange={(e) =>
              setFormData({ ...formData, baslik: e.target.value })
            }
          />
        </div>

        <div className="field">
          <label htmlFor="altBaslik">Alt Başlık</label>
          <InputText
            id="altBaslik"
            value={formData.altBaslik}
            onChange={(e) =>
              setFormData({ ...formData, altBaslik: e.target.value })
            }
          />
        </div>

        <div className="field" style={{ overflow: "visible" }}>
          <label htmlFor="aciklama">Açıklama</label>
          <Editor
            id="aciklama"
            value={formData.aciklama}
            headerTemplate={editorSettings}
            onTextChange={(e) =>
              setFormData({ ...formData, aciklama: e.htmlValue || "" })
            }
            style={{ height: "200px", overflow: "visible" }}
          />
        </div>

        <div className="field">
          <label htmlFor="sira">Sıra</label>
          <InputNumber
            id="sira"
            value={formData.sira}
            onValueChange={(e) =>
              setFormData({ ...formData, sira: e.value || 0 })
            }
          />
        </div>

        <div className="flex justify-content-end mt-4">
          <Button
            label="İptal"
            icon="pi pi-times"
            outlined
            onClick={hideDialog}
            className="mr-2"
          />
          <Button label="Kaydet" icon="pi pi-check" onClick={handleSubmit} />
        </div>
      </Dialog>

      {/* Görsel Güncelleme Dialog */}
      <Dialog
        baseZIndex={100}
        visible={gorselDialogVisible}
        header="Üst Görseli Güncelle"
        modal
        className="p-fluid w-full md:w-6"
        onHide={hideGorselDialog}
      >
        <div className="field mt-4">
          <label htmlFor="gorsel">Görsel</label>
          <FileUpload
            mode="basic"
            name="gorsel"
            accept="image/*"
            maxFileSize={8000000}
            chooseLabel="Görsel Seç"
            onSelect={(e) =>
              setGorselData({
                ...gorselData,
                kapakGorsel: e.files[0].name,
                form: e.files[0],
              })
            }
          />
        </div>

        <div className="flex justify-content-end mt-4">
          <Button
            label="İptal"
            icon="pi pi-times"
            outlined
            onClick={hideGorselDialog}
            className="mr-2"
          />
          <Button
            label="Kaydet"
            icon="pi pi-check"
            onClick={handleGorselSubmit}
          />
        </div>
      </Dialog>

      {photoZoomVisible && selectedImage && (
        <PhotoZoom
          src={selectedImage}
          onClose={() => {
            setPhotoZoomVisible(false);
            setSelectedImage(null);
          }}
        />
      )}

      <DetailPopup
        isOpen={detailPopupVisible}
        onClose={() => setDetailPopupVisible(false)}
        data={selectedDetail}
        header="İçerik Detayı"
      />
    </div>
  );
};

export default HakkimizdaYonetimi;
